// SEO Utility Functions for LocalCraft Israel

import { BASE_URL } from './constants';

// Re-export BASE_URL for convenience
export { BASE_URL } from './constants';

// Production domain (primary canonical)
const PRODUCTION_DOMAIN = 'https://localcraftisrael.com';

/**
 * Check if current domain is staging/development
 */
export function isStagingDomain(): boolean {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  return (
    hostname.includes('lovable.app') ||
    hostname.includes('localhost') ||
    hostname === '127.0.0.1'
  );
}

/**
 * Check if current path should be NOINDEX
 */
export function shouldNoIndex(path?: string): boolean {
  if (isStagingDomain()) return true;
  
  const currentPath = path || (typeof window !== 'undefined' ? window.location.pathname : '');
  
  const noIndexPaths = [
    '/dashboard',
    '/admin',
    '/orders',
    '/wishlist',
    '/cart',
    '/checkout',
    '/auth',
    '/phone-auth',
    '/seller-onboarding',
    '/dashboard-',
    '/edit-',
  ];
  
  return noIndexPaths.some(p => currentPath.startsWith(p));
}

/**
 * Generate clean canonical URL without query parameters
 * CRITICAL: Always uses production domain, no query params, no hash, lowercase
 * This is the single source of truth for all canonical URLs
 */
export function getCanonicalUrl(path?: string): string {
  if (!path) {
    // Use current location, strip query params and hash
    if (typeof window !== 'undefined') {
      const cleanPath = window.location.pathname
        .split('?')[0]
        .split('#')[0]
        .toLowerCase()
        .replace(/\/+$/, ''); // Remove trailing slashes
      return cleanPath === '' || cleanPath === '/' 
        ? PRODUCTION_DOMAIN 
        : `${PRODUCTION_DOMAIN}${cleanPath}`;
    }
    return PRODUCTION_DOMAIN;
  }
  
  // Clean the path
  const cleanPath = path
    .split('?')[0] // Remove query params
    .split('#')[0] // Remove hash
    .toLowerCase()
    .trim()
    .replace(/\/+$/, ''); // Remove trailing slashes
  
  if (!cleanPath || cleanPath === '/') {
    return PRODUCTION_DOMAIN;
  }
  
  // Ensure path starts with /
  return `${PRODUCTION_DOMAIN}${cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath}`;
}

/**
 * Convert text to SEO-friendly slug
 */
export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special chars
    .replace(/[\s_]+/g, '-')  // Replace spaces/underscores with hyphens
    .replace(/^-+|-+$/g, '');  // Remove leading/trailing hyphens
}

/**
 * Ensure image URL is absolute
 */
export function getAbsoluteImageUrl(imageUrl?: string | null): string {
  if (!imageUrl) return `${BASE_URL}/seo/default-preview.jpg`;
  
  // Already absolute
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // Relative URL
  const cleanUrl = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  return `${BASE_URL}${cleanUrl}`;
}

/**
 * Generate multilingual hreflang links with x-default
 */
export function getHreflangLinks(currentPath: string, availableLocales: string[] = ['en', 'he', 'ru', 'es']) {
  const baseUrl = getCanonicalUrl(currentPath);
  
  const links = availableLocales.map(locale => ({
    locale,
    url: baseUrl, // No query params in hreflang - clean URLs only
  }));
  
  // Add x-default pointing to English version
  links.push({
    locale: 'x-default',
    url: baseUrl,
  });
  
  return links;
}

/**
 * Truncate description to SEO-friendly length
 */
export function truncateDescription(text: string, maxLength: number = 160): string {
  if (!text) return '';
  
  const cleaned = text
    .replace(/\s+/g, ' ')
    .replace(/<[^>]*>/g, '') // Strip HTML
    .trim();
  
  if (cleaned.length <= maxLength) return cleaned;
  
  // Try to cut at sentence end
  const truncated = cleaned.slice(0, maxLength);
  const lastPeriod = truncated.lastIndexOf('.');
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastPeriod > maxLength - 30) {
    return truncated.slice(0, lastPeriod + 1);
  }
  
  if (lastSpace > maxLength - 20) {
    return truncated.slice(0, lastSpace) + '...';
  }
  
  return truncated + '...';
}

/**
 * Generate dynamic product meta description
 */
export function generateProductDescription(product: any, company: any): string {
  const title = product.title || 'handmade item';
  const sellerName = company?.name || 'Israeli artisan';
  const city = company?.city || 'Israel';
  const category = product.category || '';
  const materials = product.materials || '';
  
  let desc = `Handmade ${title}`;
  
  if (materials) {
    desc += ` crafted using ${materials}`;
  }
  
  desc += ` by ${sellerName} in ${city}`;
  
  if (category) {
    desc += `. Category: ${category}`;
  }
  
  desc += '. Delivered from Israel.';
  
  return truncateDescription(desc, 160);
}

/**
 * Generate dynamic company meta description
 */
export function generateCompanyDescription(company: any, productCount?: number): string {
  const name = company?.name || 'Israeli artisan';
  const city = company?.city || 'Israel';
  const categories = company?.categories?.slice(0, 3).join(', ') || 'handmade items';
  
  let desc = `Discover handmade items from ${name}, an artisan based in ${city}. Specialties: ${categories}.`;
  
  if (productCount) {
    desc += ` ${productCount}+ unique products.`;
  }
  
  desc += ' Shop authentic Israeli craftwork.';
  
  return truncateDescription(desc, 160);
}

/**
 * Generate dynamic category meta description
 */
export function generateCategoryDescription(category: any, productCount?: number): string {
  const name = category?.name || 'products';
  
  let desc = `Explore handmade ${name.toLowerCase()} from verified Israeli artisans.`;
  
  if (productCount) {
    desc += ` Browse ${productCount}+ unique items.`;
  }
  
  desc += ' Support local craftmakers and find authentic Israeli crafts.';
  
  return truncateDescription(desc, 160);
}

/**
 * Generate dynamic region/explore meta description
 */
export function generateRegionDescription(region: any, productCount?: number): string {
  const name = region?.name || 'Israel';
  
  let desc = `Discover artisan-made products from ${name}. Local crafts showcasing the unique culture and heritage of the region.`;
  
  if (productCount) {
    desc += ` ${productCount}+ authentic Israeli handmade items.`;
  }
  
  return truncateDescription(desc, 160);
}

/**
 * Get locale code for Open Graph
 */
export function getLocaleCode(locale: string = 'en'): string {
  const localeMap: Record<string, string> = {
    'en': 'en_US',
    'he': 'he_IL',
    'ru': 'ru_RU',
    'es': 'es_ES',
  };
  
  return localeMap[locale] || localeMap['en'];
}

/**
 * Generate breadcrumb schema
 */
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': getCanonicalUrl(item.url),
    })),
  };
}

/**
 * Generate AI-friendly seller summary
 */
export function generateSellerSummary(company: any): string {
  const name = company?.name || 'Israeli artisan';
  const city = company?.city || 'Israel';
  const categories = company?.categories?.join(', ') || 'handmade products';
  
  return `${name} creates authentic ${categories} in ${city}. Verified Israeli artisan with handcrafted items.`;
}

/**
 * Clean and validate URL slug
 */
export function cleanSlug(slug: string): string {
  if (!slug) return '';
  
  return slug
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
}

/**
 * Generate product URL from components
 */
export function generateProductUrl(companySlug: string, productSlug: string): string {
  const cleanCompanySlug = cleanSlug(companySlug);
  const cleanProductSlug = cleanSlug(productSlug);
  
  return getCanonicalUrl(`/company/${cleanCompanySlug}/product/${cleanProductSlug}`);
}

/**
 * Generate company URL from slug
 */
export function generateCompanyUrl(companySlug: string): string {
  const cleanCompanySlug = cleanSlug(companySlug);
  return getCanonicalUrl(`/company/${cleanCompanySlug}`);
}

/**
 * Extract keywords from text
 */
export function extractKeywords(text: string, maxKeywords: number = 10): string {
  if (!text) return '';
  
  // Remove common words (basic implementation)
  const commonWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been', 'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can']);
  
  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3 && !commonWords.has(word));
  
  // Count frequency
  const frequency: Record<string, number> = {};
  words.forEach(word => {
    frequency[word] = (frequency[word] || 0) + 1;
  });
  
  // Sort by frequency and take top keywords
  const keywords = Object.entries(frequency)
    .sort(([, a], [, b]) => b - a)
    .slice(0, maxKeywords)
    .map(([word]) => word);
  
  return keywords.join(', ');
}

/**
 * Generate OfferShippingDetails schema for Google Search Console compliance
 * @see https://schema.org/OfferShippingDetails
 */
export function generateShippingDetailsSchema() {
  return {
    "@type": "OfferShippingDetails",
    "shippingRate": {
      "@type": "MonetaryAmount",
      "value": "0",
      "currency": "ILS"
    },
    "shippingDestination": [
      {
        "@type": "DefinedRegion",
        "addressCountry": "IL"
      },
      {
        "@type": "DefinedRegion",
        "addressCountry": "US"
      },
      {
        "@type": "DefinedRegion",
        "addressCountry": "GB"
      }
    ],
    "deliveryTime": {
      "@type": "ShippingDeliveryTime",
      "handlingTime": {
        "@type": "QuantitativeValue",
        "minValue": 1,
        "maxValue": 3,
        "unitCode": "d"
      },
      "transitTime": {
        "@type": "QuantitativeValue",
        "minValue": 3,
        "maxValue": 14,
        "unitCode": "d"
      }
    }
  };
}

/**
 * Generate MerchantReturnPolicy schema for Google Search Console compliance
 * @see https://schema.org/MerchantReturnPolicy
 */
export function generateReturnPolicySchema() {
  return {
    "@type": "MerchantReturnPolicy",
    "applicableCountry": ["IL", "US", "GB"],
    "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
    "merchantReturnDays": 14,
    "returnMethod": "https://schema.org/ReturnByMail",
    "returnFees": "https://schema.org/FreeReturn"
  };
}

/**
 * Generate complete Product schema with all required fields for Google Search Console
 */
export function generateCompleteProductSchema(options: {
  name: string;
  description?: string;
  images: string[];
  price: number;
  currency?: string;
  url: string;
  brand?: string;
  sku?: string;
  inStock?: boolean;
  aggregateRating?: { ratingValue: string; reviewCount: number };
}) {
  const schema: Record<string, any> = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": options.name,
    "image": options.images,
    "description": options.description || options.name,
    "sku": options.sku || options.url.split('/').pop(),
    "brand": {
      "@type": "Brand",
      "name": options.brand || "LocalCraft Israel"
    },
    "offers": {
      "@type": "Offer",
      "url": options.url,
      "priceCurrency": options.currency || "ILS",
      "price": options.price || 0,
      "priceValidUntil": "2030-01-01",
      "availability": options.inStock !== false 
        ? "https://schema.org/InStock" 
        : "https://schema.org/OutOfStock",
      "shippingDetails": generateShippingDetailsSchema(),
      "hasMerchantReturnPolicy": generateReturnPolicySchema()
    }
  };

  if (options.aggregateRating && options.aggregateRating.reviewCount > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ...options.aggregateRating
    };
  }

  return schema;
}
